/* eslint-disable no-nested-ternary */
import {
  Avatar,
  Card,
  Icon,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { joinCompetition } from "apis/compete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { PERMISSIONS_NAME, COMPETITION_WIN_CONDITIONS } from "lib/constants";
import moment from "moment/moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import { checkPermission } from "utils/HelperFunctions";
import Loader from "utils/Loader";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Actions from "./Actions";
import InfoPopover from "./InfoPopover";
import DailyPointsInformationModal from "../../../Home/components/DailyPointsInformationModal";
import MoreUsers from "./MoreUsers";
import RemainingTime from "./RemainingTime";
import Row from "./Row";

const prefix = process.env.REACT_APP_CDN_ASSET_PREFIX || "/assets";
const Trophy = `${prefix}/images/badges/trophy.png`;

function ListingTable({ competitions, count, status, selectedUser, payload, setPayload, loading }) {
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [selectedCompetition, setSelectedCompetition] = useState();
  const [moreUsers, setMoreUsers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDisplayKpi, setIsDisplayKpi] = useState(false);
  const [competitionInfo, setCompetitionInfo] = useState(null);

  const handleClick = (event, leaders) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMoreUsers(leaders.slice(1));
  };

  const handleClose = () => {
    setAnchorEl();
  };

  const handlePageChange = (_, newPage) => {
    setPayload((prevPayload) => ({ ...prevPayload, pageNumber: newPage }));
    setSelectedCompetition();
  };

  const handleChangeRowsPerPage = (event) => {
    setPayload((prevPayload) => ({
      ...prevPayload,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const handleCompetitionJoin = (competitionToJoin) => {
    Swal.fire({
      title: "Join Competition",
      text: `Do you want to join this competition?`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Yes, Join!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await joinCompetition({ competitionId: competitionToJoin.id });
          try {
            if (response?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Success",
                  text: response?.data?.message || "Competition successfully joined.",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevPayload) => ({ ...prevPayload }));
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: response?.response?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  const calculateDuration = (startDatetime, endDatetime) => {
    const then = moment.tz(endDatetime, loginUser.timezone);
    const now = moment.tz(startDatetime, loginUser.timezone);
    const countdown = moment.duration(then.diff(now));
    const monthsValue = countdown.months();
    const daysValue = countdown.days();
    const hoursValue = countdown.hours();
    const minutesValue = countdown.minutes();

    let durationString = ``;

    if (monthsValue) {
      durationString += `${monthsValue} ${monthsValue > 1 ? "Months" : "Month"} ${
        daysValue ? ", " : ""
      }`;
    }

    if (daysValue) {
      durationString += `${daysValue} ${daysValue > 1 ? "Days" : "Day"} ${hoursValue ? ", " : ""}`;
    }

    if (hoursValue) {
      durationString += `${hoursValue} ${hoursValue > 1 ? "Hours" : "Hour"} ${
        minutesValue ? ", " : ""
      }`;
    }

    if (minutesValue) {
      durationString += `${minutesValue} ${minutesValue > 1 ? "Minutes" : "Minute"}`;
    }

    return durationString;
  };

  return (
    <Card sx={{ mt: 3, position: "relative" }}>
      <Loader loading={loading} />
      <Stack>
        <MDTypography variant="h5" sx={{ px: 3, py: 2 }}>
          {status}
        </MDTypography>

        {count && count > 0 ? (
          <TableContainer component={Paper} className="listing-table">
            <Table aria-label="collapsible table">
              {!isMobileDisplay && (
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell />
                    <TableCell className="listing-table-header">Name</TableCell>
                    {status !== "Upcoming" && (
                      <TableCell className="trophy-head-cell">
                        <Stack spacing={1} direction="row" alignItems="center">
                          <img src={Trophy} alt="trophy" />
                          <MDTypography className="listing-table-header">
                            {status === "Open" ? "Current Leader" : "Winner"}
                          </MDTypography>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell align="center" className="listing-table-header">
                      Participants
                    </TableCell>
                    {status === "Upcoming" && (
                      <TableCell className="listing-table-header">Duration</TableCell>
                    )}
                    {status !== "Upcoming" && (
                      <TableCell align="center" className="listing-table-header">
                        {selectedUser && selectedUser?.id !== loginUser.id
                          ? `${selectedUser?.name}'s`
                          : "My"}{" "}
                        Rank
                      </TableCell>
                    )}
                    {status === "Open" && (
                      <TableCell className="listing-table-header">Time Remaining</TableCell>
                    )}
                    {status === "Upcoming" && (
                      <TableCell className="listing-table-header">Start Time</TableCell>
                    )}
                    {status !== "Open" && (
                      <TableCell className="listing-table-header">End Time</TableCell>
                    )}
                    <TableCell />
                    {loginUser.type === "user" &&
                      status !== "Closed" &&
                      checkPermission(PERMISSIONS_NAME?.INPUT_KPIS, loginUser?.userPermission) && (
                        <TableCell />
                      )}
                  </TableRow>
                </TableHead>
              )}
              <TableBody className="listing-table-body">
                {competitions?.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      className="listing-table-row"
                      hover
                      sx={{
                        "& > *": { borderBottom: "unset" },
                        cursor: "pointer",
                        backgroundColor: row.id === selectedCompetition ? "#edf6ff" : "#fff",
                      }}
                      onClick={() => {
                        if (row.id === selectedCompetition) {
                          setSelectedCompetition();
                        } else {
                          setSelectedCompetition(row.id);
                        }
                      }}
                    >
                      <TableCell width="5%">
                        <img className="competition-badge" src={row?.Badge?.badgeUrl} alt="badge" />
                      </TableCell>
                      <TableCell>{row?.name}</TableCell>
                      {isMobileDisplay ? (
                        <TableCell width="5%" sx={{ fontSize: "1.25rem" }}>
                          {row.id === selectedCompetition ? (
                            <ExpandMoreIcon />
                          ) : (
                            <ChevronRightIcon />
                          )}
                        </TableCell>
                      ) : (
                        <>
                          {status !== "Upcoming" && (
                            <TableCell className="trophy-row-cell" align="center">
                              {row?.currentLeader?.length > 0 &&
                              (status === "Open" ||
                                (status === "Closed" && row.showCompetitionRank)) ? (
                                <Stack spacing={1} direction="row" alignItems="center">
                                  <Avatar
                                    variant="circular"
                                    src={row?.currentLeader?.[0]?.profilePicture}
                                    alt="profile-picture"
                                    sx={{ imageRendering: "-webkit-optimize-contrast" }}
                                  />
                                  <MDTypography>
                                    {row?.currentLeader?.[0]?.name || "-"}
                                  </MDTypography>
                                  {row?.currentLeader?.length > 1 && (
                                    <Tooltip
                                      title={`Click to see other ${
                                        status === "Open" ? "Leaders" : "Winners"
                                      }`}
                                    >
                                      <MDBox
                                        sx={{ display: "flex", ml: 1 }}
                                        onClick={(event) => handleClick(event, row.currentLeader)}
                                      >
                                        <MoreHorizIcon />
                                      </MDBox>
                                    </Tooltip>
                                  )}
                                </Stack>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          )}
                          <TableCell align="center">{row?.competitionList?.length || 0}</TableCell>
                          {status === "Upcoming" && (
                            <TableCell>
                              {row.endDatetime
                                ? calculateDuration(row.startDatetime, row.endDatetime)
                                : "-"}
                            </TableCell>
                          )}
                          {status !== "Upcoming" && (
                            <TableCell align="center">
                              {row.type === "tremendous payout" &&
                              (status === "Open" ||
                                (status === "Closed" && row.showCompetitionRank)) &&
                              row.competitionList?.find(
                                (userInCompetition) => userInCompetition.userId === loginUser.id
                              )
                                ? typeof row.myRank !== "undefined"
                                  ? row.myRank
                                  : "-"
                                : "-"}
                            </TableCell>
                          )}
                          {status === "Open" && (
                            <TableCell>
                              {row?.endDatetime ? <RemainingTime endDate={row.endDatetime} /> : "-"}
                            </TableCell>
                          )}
                          {status === "Upcoming" && (
                            <TableCell>
                              {moment
                                .tz(row.startDatetime, loginUser.timezone)
                                .format("dddd MMMM D, hh:mm a")}
                            </TableCell>
                          )}
                          {status !== "Open" && (
                            <TableCell>
                              {row?.endDatetime
                                ? moment
                                    .tz(row.endDatetime, loginUser.timezone)
                                    .format("dddd MMMM D, hh:mm a")
                                : "-"}
                            </TableCell>
                          )}
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              className="icon-stack"
                            >
                              <Tooltip
                                title={
                                  row.type === "tremendous payout"
                                    ? "Tremendous Payout Competition"
                                    : "Unranked Competition"
                                }
                              >
                                <Icon>
                                  {row.type === "tremendous payout"
                                    ? "security"
                                    : "remove_moderator"}
                                </Icon>
                              </Tooltip>
                              <Tooltip
                                title={
                                  row.joinable ? "Joinable Competition" : "Not Joinable Competition"
                                }
                              >
                                <Icon>{row.joinable ? "lock_open" : "lock"}</Icon>
                              </Tooltip>
                              <Tooltip title="View KPIs">
                                <Icon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCompetitionInfo(row);
                                    setIsDisplayKpi(true);
                                  }}
                                >
                                  view_headline
                                </Icon>
                              </Tooltip>
                              <InfoPopover competition={row} />
                            </Stack>
                          </TableCell>
                          {loginUser.type === "user" &&
                          status !== "Closed" &&
                          checkPermission(
                            PERMISSIONS_NAME?.INPUT_KPIS,
                            loginUser?.userPermission
                          ) ? (
                            <TableCell align="center">
                              {row.joinable &&
                              !row.competitionList?.find(
                                (userInCompetition) => userInCompetition.userId === loginUser.id
                              ) ? (
                                <Actions
                                  competition={row}
                                  joinCompetition={handleCompetitionJoin}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          ) : row.competitionList?.find(
                              (userInCompetition) => userInCompetition.userId === loginUser.id
                            ) ? null : null}
                        </>
                      )}
                    </TableRow>
                    <TableRow>
                      <Row
                        competition={row}
                        status={status}
                        selectedCompetition={selectedCompetition}
                        handleClick={handleClick}
                        calculateDuration={calculateDuration}
                        setIsDisplayKpi={setIsDisplayKpi}
                        setCompetitionInfo={setCompetitionInfo}
                        selectedUser={selectedUser}
                        handleCompetitionJoin={handleCompetitionJoin}
                      />
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 25, 50]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
              component="div"
              count={count}
              rowsPerPage={payload?.pageSize || 10}
              page={payload?.pageNumber || 0}
              sx={{ flex: "1 1 auto" }}
            />
          </TableContainer>
        ) : (
          <Typography
            sx={{ p: "0 0 1rem 1.5rem", fontSize: "0.9rem", fontWeight: "400" }}
          >{`There are no ${status} competitions`}</Typography>
        )}
      </Stack>
      <DailyPointsInformationModal
        open={isDisplayKpi}
        handleClose={() => {
          setCompetitionInfo(null);
          setIsDisplayKpi(false);
        }}
        competition
        actionInfo={competitionInfo?.KpiTemplate?.KpiTemplatesActivities}
        showCountAndPoints={
          competitionInfo?.CompetitionWinCondition?.condition ===
          COMPETITION_WIN_CONDITIONS.TOTAL_POINTS
        }
        loading={loading}
      />
      <MoreUsers items={moreUsers} anchorEl={anchorEl} handleClose={handleClose} />
    </Card>
  );
}

export default ListingTable;
